import { type FC, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  SpreadsheetEditor,
  type SpreadsheetData,
  type SpreadsheetContextMenuItem,
  SpreadsheetActionType,
} from "@via/components/spreadsheet-editor/spreadsheet-editor.tsx";
import { type UseFormReturn } from "react-hook-form";
import {
  fetchInputFileList,
  fetchVmetaDatasetFiles,
} from "@via/pages/projects/runs/runs.api.tsx";
import {
  type SaveRunInputs,
  SaveRunInputVmetaCollection,
  InputType,
} from "common";
import { useVmetaDatasetFiles } from "@via/pages/projects/runs/overview/hooks/useVmetaDatasetFiles";
import { buildVmetaDatasetMenuItems } from "@via/pages/projects/runs/overview/utils/vmeta-dataset-menu-items";

interface SingleFileSpreadsheetProps {
  form: UseFormReturn;
  data?: string[][];
  columns?: string[];
  updateColumns: (columnIndex: number, columns: string[]) => void;
  collectionAutofill?: {
    column: string;
    input_name: string;
  };
  onChange?: (data: string[][]) => void;
  isLoading?: boolean;
  insertColumnLeft?: (columnIndex: number, columns: string[]) => void;
  insertColumnRight?: (columnIndex: number, columns: string[]) => void;
  deleteColumn?: (columnIndex: number) => void;
  paramsInputs?: SaveRunInputs[];
}

export const isVmetaCollection = (
  input: SaveRunInputs | undefined
): input is SaveRunInputVmetaCollection =>
  !!input && input.type === InputType.VmetaCollection;

const SingleFileSpreadsheet: FC<SingleFileSpreadsheetProps> = ({
  form,
  data,
  columns,
  updateColumns,
  collectionAutofill,
  onChange,
  isLoading = false,
  insertColumnLeft,
  insertColumnRight,
  deleteColumn,
  paramsInputs,
}) => {
  const { runId = "" } = useParams();
  const { watch } = form;
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>([]);
  const [spreadsheetLoading, setSpreadsheetLoading] = useState(isLoading);
  const spreadsheetColumns = columns?.length ? columns : ["Column1", "Column2"];
  const { vmetaFilesByCollectionId } = useVmetaDatasetFiles(paramsInputs);

  const fetchCollectionAutofill = useCallback(async () => {
    const autofillInputName = collectionAutofill?.input_name;
    const columnToAdd = parseInt(collectionAutofill?.column || "1", 10);

    if (autofillInputName) {
      setSpreadsheetLoading(true);
      const collectionName = watch(`params.${autofillInputName}`);
      const selectedInput = Array.isArray(paramsInputs)
        ? paramsInputs.find(
            input =>
              input.name === autofillInputName && input.value === collectionName
          )
        : undefined;

      let autofillCollections: string[] = [];

      if (isVmetaCollection(selectedInput) && selectedInput.vmetaCollectionId) {
        const { data: vmetaDatasetFiles } = await fetchVmetaDatasetFiles(
          selectedInput.vmetaCollectionId
        );
        if (Array.isArray(vmetaDatasetFiles)) {
          autofillCollections = vmetaDatasetFiles.map(file => file.name);
        }
      } else {
        const collectionFiles = await fetchInputFileList(runId);

        autofillCollections = collectionFiles
          .filter(file =>
            file?.collectionName?.split(",")?.includes(collectionName)
          )
          .map(({ name }) => name);
      }

      setSpreadsheetData(prevData =>
        prevData.map((row, rowIndex) =>
          row.map((cell, colIndex) =>
            colIndex === columnToAdd - 1 &&
            rowIndex < autofillCollections.length
              ? { value: autofillCollections[rowIndex] }
              : cell
          )
        )
      );
      setSpreadsheetLoading(false);
    }
  }, [collectionAutofill]);

  useEffect(() => {
    if (data && data?.length > 0) {
      const columns = data[0];
      const content = data.slice(1);
      const maxRows = Math.max(content.length, 100);

      setSpreadsheetData(
        Array.from({ length: maxRows }, (_, rowIndex) =>
          columns.map((_, colIndex) => ({
            value: content?.[rowIndex]?.[colIndex] ?? "",
          }))
        )
      );
    } else {
      setSpreadsheetData(
        Array.from({ length: 100 }, () =>
          Array.from({ length: spreadsheetColumns.length }, () => ({
            value: "",
          }))
        )
      );
      fetchCollectionAutofill();
    }
  }, [data]);

  useEffect(() => {
    if (spreadsheetData.length > 0) {
      updateSpreadsheetData(spreadsheetData);
    }
  }, [spreadsheetData]);

  const updateSpreadsheetData = (data: SpreadsheetData) => {
    const transformedData = data.map(row =>
      row.map(cell => cell.value as string)
    );
    // Find the last non-empty row
    const lastNonEmptyIndex = transformedData.reduceRight(
      (lastIndex, row, index) => {
        return row.some(cell => cell.trim() !== "")
          ? Math.max(lastIndex, index)
          : lastIndex;
      },
      -1
    );

    const spreadsheetContent = transformedData.slice(0, lastNonEmptyIndex + 1);

    onChange?.([spreadsheetColumns, ...spreadsheetContent]);
  };

  const getSpreadsheetContextMenuItems = (): SpreadsheetContextMenuItem[] => {
    const autofillInputName = collectionAutofill?.input_name;
    const collectionName = watch(`params.${autofillInputName}`);
    const isDatasetVmetaCollection = paramsInputs?.find(
      p => isVmetaCollection(p) && p.vmetaCollectionId
    );
    const vmetaDatasetMenuItems = buildVmetaDatasetMenuItems(
      vmetaFilesByCollectionId
    );

    return [
      ...vmetaDatasetMenuItems,
      ...(autofillInputName && collectionName && !isDatasetVmetaCollection
        ? [
            {
              label: `Insert File Names from "${collectionName}"`,
              type: SpreadsheetActionType.PasteCollectionNames,
              onClick: async () => {
                const selectedInput = Array.isArray(paramsInputs)
                  ? paramsInputs.find(
                      input =>
                        input.name === autofillInputName &&
                        input.value === collectionName
                    )
                  : undefined;

                let autofillCollections: string[] = [];
                if (
                  isVmetaCollection(selectedInput) &&
                  selectedInput.vmetaCollectionId
                ) {
                  const { data: vmetaDatasetFiles } =
                    await fetchVmetaDatasetFiles(
                      selectedInput.vmetaCollectionId
                    );
                  if (Array.isArray(vmetaDatasetFiles)) {
                    autofillCollections = vmetaDatasetFiles.map(
                      file => file.name
                    );
                  }
                } else {
                  const collectionFiles = await fetchInputFileList(runId);

                  autofillCollections = collectionFiles
                    .filter(file =>
                      file?.collectionName?.split(",")?.includes(collectionName)
                    )
                    .map(({ name }) => name);
                }

                return autofillCollections;
              },
            },
          ]
        : []),
      {
        label: "Rename Column Header",
        type: SpreadsheetActionType.RenameColumnHeaders,
        onClick: async (columnIndex: number) => {
          updateColumns(columnIndex, spreadsheetColumns || []);
          return [];
        },
      },
      {
        label: "Insert Column Left",
        type: SpreadsheetActionType.InsertColumnLeft,
        onClick: async (columnIndex: number) => {
          insertColumnLeft?.(columnIndex, spreadsheetColumns || []);
          return [];
        },
      },
      {
        label: "Insert Column Right",
        type: SpreadsheetActionType.InsertColumnRight,
        onClick: async (columnIndex: number) => {
          insertColumnRight?.(columnIndex, spreadsheetColumns || []);
          return [];
        },
      },
      {
        label: "Delete Column",
        type: SpreadsheetActionType.DeleteColumn,
        onClick: async (columnIndex: number) => {
          deleteColumn?.(columnIndex);
          return [];
        },
      },
    ];
  };

  return (
    <div className="mb-5">
      <SpreadsheetEditor
        columnLabels={spreadsheetColumns}
        data={spreadsheetData}
        onChange={updateSpreadsheetData}
        isLoading={isLoading || spreadsheetLoading}
        extraContextMenuItems={getSpreadsheetContextMenuItems()}
      />
    </div>
  );
};

export default SingleFileSpreadsheet;
